import { AxisBottom, AxisLeft } from "@visx/axis";
import { Group } from "@visx/group";
import { scaleBand, scaleLinear } from "@visx/scale";
import { Bar } from "@visx/shape";
import { Divider } from "primereact/divider";
import { Tooltip } from "primereact/tooltip";
import React from "react";
import * as client from "../../client";
import { formatAbbreviatedMoney } from "../../pages/specialty-assistant/Money";
import { useRandomIds } from "../../util";
import styles from "./Chart.module.css";

const palette: {
  [key in client.color_code]: { base: string; light: string; stroke: string };
} = {
  green: {
    base: "rgba(82, 196, 120, 0.7)",
    light: "rgba(96, 210, 135, 0.6)",
    stroke: "#16a34a",
  },
  yellow: {
    base: "rgba(236, 191, 69, 0.7)",
    light: "rgba(243, 202, 90, 0.6)",
    stroke: "#d97706",
  },
  red: {
    base: "rgba(242, 111, 122, 0.7)",
    light: "rgba(248, 132, 142, 0.6)",
    stroke: "#e11d48",
  },
  blue: {
    base: "rgba(90, 151, 245, 0.7)",
    light: "rgba(120, 170, 250, 0.6)",
    stroke: "#2563eb",
  },
  purple: {
    base: "rgba(147, 120, 245, 0.7)",
    light: "rgba(165, 140, 250, 0.6)",
    stroke: "#7c3aed",
  },
  teal: {
    base: "rgba(46, 196, 182, 0.7)",
    light: "rgba(66, 210, 196, 0.6)",
    stroke: "#0d9488",
  },
  gray: {
    base: "rgba(120, 130, 150, 0.55)",
    light: "rgba(140, 150, 165, 0.45)",
    stroke: "#475569",
  },
  orange: {
    base: "rgba(251, 146, 60, 0.7)",
    light: "rgba(253, 166, 85, 0.6)",
    stroke: "#ea580c",
  },
  pink: {
    base: "rgba(244, 114, 182, 0.7)",
    light: "rgba(249, 135, 197, 0.6)",
    stroke: "#db2777",
  },
  indigo: {
    base: "rgba(99, 102, 241, 0.7)",
    light: "rgba(129, 132, 250, 0.6)",
    stroke: "#4f46e5",
  },
  cyan: {
    base: "rgba(34, 211, 238, 0.7)",
    light: "rgba(63, 220, 243, 0.6)",
    stroke: "#0891b2",
  },
  lime: {
    base: "rgba(163, 230, 53, 0.7)",
    light: "rgba(180, 238, 80, 0.6)",
    stroke: "#65a30d",
  },
} as const;

const ellipsize = (s: string, max = 10) =>
  s.length > max ? `${s.slice(0, max - 1)}…` : s;

const niceMax = (vals: number[]) => {
  const m = Math.max(0, ...vals);
  return m === 0 ? 1 : m * 1.05; // small headroom
};

const MIN_W = 220;
const MIN_H = 160;

const BarChart = ({
  data,
  width,
  height,
  title,
}: {
  data: client.DataPoint[];
  width: number;
  height: number;
  title: string;
}) => {
  // enforce minimums so axes have somewhere to live
  const w = Math.max(width, MIN_W);
  const h = Math.max(height, MIN_H);
  const tooltipIds = useRandomIds(data.length);

  // base margins
  let margin = { top: 8, right: 8, bottom: 28, left: 28 };

  // compute scales on inner area
  let innerWidth = Math.max(0, w - margin.left - margin.right - 20);
  const innerHeight = Math.max(0, h - margin.top - margin.bottom);

  if (innerWidth === 0 || innerHeight === 0) {
    // if parent gave us literally no space, render nothing
    return null;
  }

  const xDomain = data.map((d) => d.label);
  const yMax = niceMax(data.map((d) => d.value));

  const xScale = scaleBand<string>({
    domain: xDomain,
    range: [0, innerWidth],
    padding: 0.3,
  });

  const yScale = scaleLinear<number>({
    domain: [0, yMax],
    range: [innerHeight, 0],
    nice: true,
  });

  // ---- adaptive ticks ----
  // y-axis: ~40px per tick looks decent
  const yTicks = Math.max(2, Math.min(6, Math.floor(innerHeight / 40)));

  // x-axis: limit number of category ticks based on ~70px per tick label
  const maxXTicks = Math.max(2, Math.floor(innerWidth / 70));
  const xTickValues =
    xDomain.length <= maxXTicks
      ? xDomain
      : xDomain.filter(
          (_, i) => i % Math.ceil(xDomain.length / maxXTicks) === 0
        );

  // estimate widest y tick to bump left margin a bit (rough heuristic: ~7px per char)
  const yTickSamples = yScale
    .ticks(yTicks)
    .map((t) => formatAbbreviatedMoney(t, false));
  const longestY = yTickSamples.reduce(
    (a, b) => (b.length > a.length ? b : a),
    ""
  );
  const estLeft = Math.min(80, 7 * longestY.length + 16);
  if (estLeft > margin.left) {
    margin = { ...margin, left: estLeft };
    innerWidth = Math.max(0, w - margin.left - margin.right);
  }

  return (
    <div className={styles.verticalContainerSmall}>
      <span className={styles.chartTitle}>{title}</span>
      <svg width={w} height={h}>
        <defs>
          {Object.entries(palette).map(([name, c]) => (
            <linearGradient
              id={`grad-${name}`}
              key={name}
              x1="0%"
              y1="0%"
              x2="0%"
              y2="100%"
            >
              <stop offset="0%" stopColor={c.base} stopOpacity={0.95} />
              <stop offset="100%" stopColor={c.light} stopOpacity={0.85} />
            </linearGradient>
          ))}
          {/* clip to plotting area so long labels don't leak */}
          <clipPath id="plot-clip">
            <rect x="0" y="0" width={innerWidth} height={innerHeight} />
          </clipPath>
        </defs>

        <Group left={margin.left} top={margin.top}>
          {/* bars */}
          <g clipPath="url(#plot-clip)">
            {data.map((d, index) => {
              const x = xScale(d.label) ?? 0;
              const bw = xScale.bandwidth();
              const y = yScale(d.value);
              const barH = innerHeight - y;
              const gradKey = d.color_code;
              const fillId = `grad-${gradKey}`;
              const stroke = palette[gradKey].stroke;
              const tooltipId = tooltipIds[index];

              // label above bar—keep it inside the plot
              const labelY = Math.max(12, y - 6);
              return (
                <g key={d.label}>
                  <Tooltip target={`.${tooltipId}`} />
                  <Bar
                    className={tooltipId}
                    x={x}
                    y={y}
                    width={bw}
                    height={barH}
                    fill={`url(#${fillId})`}
                    stroke={stroke}
                    strokeWidth={0.5}
                    rx={2}
                    ry={2}
                    data-pr-tooltip={`${d.label}`}
                  />
                  <text
                    x={x + bw / 2}
                    y={labelY}
                    textAnchor="middle"
                    fill="var(--text-color-secondary)"
                    fontSize={11}
                    fontWeight={600}
                    fontFamily="Inter, system-ui, sans-serif"
                  >
                    {d.type === "monetary"
                      ? `${formatAbbreviatedMoney(d.value)}`
                      : formatAbbreviatedMoney(d.value, false)}
                  </text>
                </g>
              );
            })}
          </g>

          {/* axes */}
          <AxisBottom
            scale={xScale}
            top={innerHeight}
            tickValues={xTickValues}
            tickFormat={(v) => {
              const barWidth = xScale.bandwidth();
              // Rough estimate: ~6px per character for fontSize 11
              // Use 90% of bar width to leave some padding
              const maxChars = Math.floor((barWidth * 0.9) / 6);
              // Ensure at least 3 characters show
              return ellipsize(String(v), Math.max(3, maxChars));
            }}
            tickLabelProps={() => ({
              fill: "var(--text-color-secondary)",
              fontSize: 11,
              fontFamily: "Inter, system-ui, sans-serif",
              textAnchor: "middle",
              dy: "0.5em",
            })}
            stroke="#e2e8f0"
            tickStroke="#e2e8f0"
          />

          <AxisLeft
            scale={yScale}
            numTicks={yTicks}
            tickFormat={(v) => formatAbbreviatedMoney(Number(v), false)}
            tickLabelProps={() => ({
              fill: "var(--text-color-secondary)",
              fontSize: 11,
              fontFamily: "Inter, system-ui, sans-serif",
              textAnchor: "end",
              dx: "-0.25em",
              dy: "0.25em",
            })}
            stroke="#e2e8f0"
            tickStroke="#e2e8f0"
          />
        </Group>
      </svg>
    </div>
  );
};

type ChartGroupProps = {
  chartGroups: client.ChartGroup[];
  chartWidth?: number;
  chartHeight?: number;
};

export const ChartGroup = ({
  chartGroups = [],
  chartWidth = 540,
  chartHeight = 300,
}: ChartGroupProps) => {
  return (
    <div className={styles.verticalContainer}>
      {chartGroups.map((chartGroup) => (
        <React.Fragment key={chartGroup.title}>
          <span className={styles.header}>{chartGroup.title}</span>
          <div className={styles.chartContainer}>
            {chartGroup.charts.map((chart) => (
              <BarChart
                data={chart.data}
                width={chartWidth}
                height={chartHeight}
                title={chart.title}
                key={chart.title}
              />
            ))}
          </div>
          <Divider />
        </React.Fragment>
      ))}
    </div>
  );
};
